import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { trackPurchase } from "../utils/metaPixel";

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const purchaseTrackedRef = useRef(false);

  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 6;
    const POLL_INTERVAL = 2000;

    const verifyPayment = async () => {
      if (!sessionId || !token) {
        setStatus("error");
        return;
      }

      try {
        const res = await fetch(`${API}/api/payments/stripe/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) {
          if (!cancelled) setStatus("error");
          return;
        }

        const data = await res.json();
        if (cancelled) return;
        setPaymentInfo(data);

        if (data.payment_status === "paid") {
          setStatus("success");
          // Disparar Meta Pixel Purchase UNA SOLA VEZ cuando Stripe confirma el pago.
          // Esto evita inflar métricas con reservas no pagadas/abandonadas.
          if (!purchaseTrackedRef.current) {
            purchaseTrackedRef.current = true;
            trackPurchase(Number(data.amount || 0), bookingId, "Reserva Plazafy");
          }
          return;
        }

        if (data.status === "expired") {
          setStatus("error");
          return;
        }

        // Aún pendiente: re-intentar (polling)
        attempts += 1;
        if (attempts < MAX_ATTEMPTS) {
          setTimeout(verifyPayment, POLL_INTERVAL);
        } else {
          setStatus("error");
        }
      } catch (error) {
        if (!cancelled) setStatus("error");
      }
    };

    verifyPayment();

    return () => { cancelled = true; };
  }, [sessionId, token, bookingId]);

  return (
    <div className="min-h-screen bg-[#0d0b12] flex items-center justify-center p-4">
      <div className="bg-[#1a1625] border border-white/10 rounded-2xl p-8 max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 text-violet-500 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Verificando pago...</h1>
            <p className="text-gray-400">Por favor, espera un momento</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">¡Pago completado!</h1>
            <p className="text-gray-400 mb-6">
              Tu reserva ha sido confirmada. Recibirás un email con los detalles.
            </p>
            {paymentInfo && (
              <div className="bg-white/5 rounded-lg p-4 mb-6">
                <p className="text-gray-400 text-sm">Importe pagado</p>
                <p className="text-2xl font-bold text-green-400">
                  {paymentInfo.amount?.toFixed(2)}€
                </p>
              </div>
            )}
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/my-bookings")}
                className="w-full bg-violet-500 hover:bg-violet-600 text-white"
              >
                Ver mis reservas
              </Button>
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                Volver al inicio
              </Button>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Error en el pago</h1>
            <p className="text-gray-400 mb-6">
              No hemos podido verificar tu pago. Por favor, inténtalo de nuevo o contacta con soporte.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate("/my-bookings")}
                className="w-full bg-violet-500 hover:bg-violet-600 text-white"
              >
                Ver mis reservas
              </Button>
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                Volver al inicio
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
